/**
 * Skillsnap TypeScript Types
 * Requirements: Data Models from design.md
 */

// Job Phases
export type JobPhase =
  | 'Queued' | 'Generating' | 'Ready'
  | 'Applied' | 'Follow-Up' | 'Negotiation' | 'Declined' | 'Accepted'
  | 'Skipped' | 'Expired' | 'Errored' | 'Trash';

export const VALID_PHASES: JobPhase[] = [
  'Queued', 'Generating', 'Ready',
  'Applied', 'Follow-Up', 'Negotiation', 'Declined', 'Accepted',
  'Skipped', 'Expired', 'Errored', 'Trash'
];

export const ACTIVE_PHASES: JobPhase[] = [
  'Queued', 'Generating', 'Ready',
  'Applied', 'Follow-Up', 'Negotiation'
];

// Generation States
export type GenerationState = 'locked' | 'ready' | 'generating' | 'complete' | 'error' | 'retry';

// Generation Types
export type GenerationType = 'manual' | 'embedding' | 'generate' | 'omit';

// Subcomponent Types
export type SubcomponentType =
  | 'contact' | 'summary' | 'skills' | 'highlights'
  | 'experience' | 'education' | 'awards' | 'presentations'
  | 'publications' | 'portfolios' | 'coverletter' | 'elevatorpitch';

export const SUBCOMPONENTS: SubcomponentType[] = [
  'contact', 'summary', 'highlights', 'skills',
  'experience', 'education', 'awards', 'presentations',
  'publications', 'portfolios', 'coverletter', 'elevatorpitch'
];

// Default section order for resume rendering (excludes coverletter which is separate)
export const DEFAULT_SECTION_ORDER: SubcomponentType[] = [
  'contact', 'summary', 'highlights', 'skills',
  'experience', 'education', 'awards', 'presentations',
  'publications', 'portfolios'
];

// Generation type restrictions per component
// Components not listed here allow all types: manual, embedding, generate, omit
export const GENERATION_TYPE_RESTRICTIONS: Partial<Record<SubcomponentType, GenerationType[]>> = {
  contact: ['manual', 'omit'],
  summary: ['generate', 'manual', 'omit'],
  skills: ['embedding', 'manual', 'omit'],
  highlights: ['generate', 'omit'],
  experience: ['generate', 'embedding', 'manual', 'omit'],
  education: ['embedding', 'manual', 'omit'],
  awards: ['embedding', 'manual', 'omit'],
  presentations: ['embedding', 'manual', 'omit'],
  publications: ['embedding', 'manual', 'omit'],
  portfolios: ['embedding', 'manual', 'omit'],
  coverletter: ['generate', 'omit'],
  elevatorpitch: ['generate', 'omit'],
};

// Contact Item (for list of contact methods)
export interface ContactItem {
  icon: 'email-at' | 'phone' | 'phone-volume' | 'linkedin' | 'x-twitter' | 'bluesky' | 'github' | 'github-square' | 'globe' | 'house' | 'facebook' | 'discord' | 'slack' | 'telegram' | 'whatsapp' | 'signal-chat' | 'skillsnap';
  title: string;
  url: string;
  include?: string;
}

// Skill object (was bare string)
export interface Skill {
  title: string;
  tags: string[];
  include?: string;
}

// Experience Bullet
export interface ExperienceBullet {
  text: string;
  tags: string[];
  include?: string;
}

// Experience Role
export interface ExperienceRole {
  title: string;
  startDate: string;
  endDate?: string;
  current: boolean;
  location?: string;
  bullets: ExperienceBullet[];
  include?: string;
}

// Experience Company
export interface ExperienceCompany {
  company: string;
  url: string[];
  employees?: string;
  startDate: string;
  endDate?: string;
  current: boolean;
  location?: string;
  description?: string;
  roles: ExperienceRole[];
  include?: string;
}

// Education Entry
export interface Education {
  title: string;
  org: string;
  url?: string;
  year: string;
  tags: string[];
  include?: string;
}

// Award Entry
export interface Award {
  title: string;
  issuer: string;
  reward: string;
  url?: string;
  year: string;
  tags: string[];
  include?: string;
}

// Presentation Entry (formerly Keynote)
export interface Presentation {
  title: string;
  event: string;
  url?: string;
  year: string;
  tags: string[];
  include?: string;
}

// Publication Entry
export interface Publication {
  title: string;
  url?: string;
  year: string;
  tags: string[];
  include?: string;
}

// Portfolio Entry
export interface Portfolio {
  title: string;
  url?: string;
  year: string;
  tags: string[];
  include?: string;
}

// Summary Phrase (for consistent summary generation)
export interface SummaryPhrase {
  text: string;
  tags: string[];
  include?: string;
}

// Recommendation from AI Resume Review
export interface Recommendation {
  id: string;
  path: string;
  field: string;
  desc: string;
  action: 'replace' | 'append' | 'remove';
  data: unknown;
  // Legacy field for backwards compatibility with old recommendations
  replacement?: Record<string, unknown>;
  status: 'pending' | 'accepted' | 'dismissed';
  created_at: string;
}

// Resume JSON
export interface ResumeJSON {
  name: string;
  contact: ContactItem[];
  summary: string;
  skills: Skill[];
  highlights: string[];
  experience: ExperienceCompany[];
  education: Education[];
  awards: Award[];
  presentations: Presentation[];
  publications: Publication[];
  portfolios: Portfolio[];
  summary_phrases?: SummaryPhrase[];
  recommendations?: Recommendation[];
}

// Resume
export interface Resume {
  userid: string;
  resumename: string;
  resumejson: ResumeJSON;
  lastupdate: string;
  master_resume_path?: string;
  publish_master_resume?: boolean;
}

// Job
export interface Job {
  jobid: string;
  postedts: string;
  jobcompany: string;
  joblistid?: string;
  jobtitle: string;
  jobtitlesafe: string;
  jobdesc: string;
  joblocation?: string;
  jobsalary?: string;
  jobposteddate: string;
  joburl?: string;
  jobcompanylogo?: string;
  jobtags: string[];
  jobreqid?: string;
  createdAt: string;
}

// User Job (combined with Job for display)
export interface UserJob extends Job {
  userid: string;
  resumeid?: string;
  jobphase: JobPhase;
  
  // Subcomponent data
  datacontact?: string;
  datasummary?: string;
  dataskills?: string;
  datahighlights?: string;
  dataexperience?: string;
  dataeducation?: string;
  dataawards?: string;
  datapresentations?: string;
  datapublications?: string;
  dataportfolios?: string;
  datacoverletter?: string;
  dataelevatorpitch?: string;

  // Generation states
  statecontact: GenerationState;
  statesummary: GenerationState;
  stateskills: GenerationState;
  statehighlights: GenerationState;
  stateexperience: GenerationState;
  stateeducation: GenerationState;
  stateawards: GenerationState;
  statepresentations: GenerationState;
  statepublications: GenerationState;
  stateportfolios: GenerationState;
  statecoverletter: GenerationState;
  stateelevatorpitch: GenerationState;

  // Generation types
  typecontact: GenerationType;
  typesummary: GenerationType;
  typeskills: GenerationType;
  typehighlights: GenerationType;
  typeexperience: GenerationType;
  typeeducation: GenerationType;
  typeawards: GenerationType;
  typepresentations: GenerationType;
  typepublications: GenerationType;
  typeportfolios: GenerationType;
  typecoverletter: GenerationType;
  typeelevatorpitch: GenerationType;

  // Final file locations
  s3locresumehtml?: string;
  s3locresumepdf?: string;
  s3loccoverletterhtml?: string;
  s3loccoverletterpdf?: string;
  s3locresumetxt?: string;
  s3loccoverlettertxt?: string;
  s3locresumetemplate?: string;

  // Feedback on generated files
  feedback_resume?: 'U' | 'D';
  feedback_coverletter?: 'U' | 'D';

  // Calculated fields
  postingAge?: number;
  updatedAt: string;
}

// --- New orthogonal user model ---
// Role: permissions/access level
export type Role = 'user' | 'admin' | 'snappy' | 'test';

// Status: feature gating
export type Status = 'trial' | 'active' | 'hibernating' | 'cancelled' | 'expired';

// Plan: billing model
export type Plan = 'free' | 'consumer' | 'enterprise';


// User Preferences
export interface UserPreferences {
  default_gen_contact: GenerationType;
  default_gen_summary: GenerationType;
  default_gen_skills: GenerationType;
  default_gen_highlights: GenerationType;
  default_gen_experience: GenerationType;
  default_gen_education: GenerationType;
  default_gen_awards: GenerationType;
  default_gen_presentations: GenerationType;
  default_gen_publications: GenerationType;
  default_gen_portfolios: GenerationType;
  default_gen_coverletter: GenerationType;
  // Resume Generation Options
  show_skillsnap_url?: boolean;
  show_year_education?: boolean;
  show_year_awards?: boolean;
  show_year_presentations?: boolean;
  show_year_publications?: boolean;
  show_year_portfolios?: boolean;
  cutoff_year?: number;
  target_pages?: number | null;
  bullet_period_style?: string;
  tutorial_completed?: boolean;
  marketing_email_optin?: boolean;
  section_names?: string;
  section_order?: string;
  admin_timezone?: string;
  // App Options - Active Phases (Days before moving to Expired)
  days_queued_before_expired?: number;
  days_generating_before_expired?: number;
  days_ready_before_expired?: number;
  days_applied_before_expired?: number;
  days_followup_before_expired?: number;
  days_negotiation_before_expired?: number;
  days_accepted_before_expired?: number;
  days_declined_before_expired?: number;
  // App Options - Terminal Phases (Days before Trash/Delete)
  days_skipped_before_trashed?: number;
  days_errored_before_trashed?: number;
  days_expired_before_trashed?: number;
  days_trash_before_deleted?: number;
  // New orthogonal user model
  role?: Role;
  status?: Status;
  plan?: Plan;
  stripe_customer_id?: string;
  org_id?: string;
  org_name?: string;
  // Usage tracking
  jobs_created_this_month?: number;
  monthly_job_limit?: number;
  // Watermark & name link settings
  add_skillsnap_watermark?: boolean;
  link_name_to_url?: boolean;
  master_resume_format?: 'html' | 'pdf';
  // Per-user overrides (from admin)
  allowed_master_resumes?: number | null;
  allowed_job_cards_per_month?: number | null;
}

// API Response Types
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface JobListResponse {
  jobs: UserJob[];
  count: number;
  phaseCounts: Record<JobPhase, number>;
  totalCount: number;
}

export interface ResumeListResponse {
  resumes: Resume[];
  count: number;
}

export interface GenerationStatusResponse {
  jobid: string;
  phase: JobPhase;
  overallStatus: 'ready' | 'generating' | 'complete' | 'error';
  subcomponents: Record<SubcomponentType, {
    state: GenerationState;
    type: GenerationType;
    hasContent: boolean;
  }>;
  finalFiles: {
    resumeHtml: { ready: boolean; generated: boolean; s3loc: string };
    resumePdf: { ready: boolean; generated: boolean; s3loc: string };
    coverLetterHtml: { ready: boolean; generated: boolean; s3loc: string };
    coverLetterPdf: { ready: boolean; generated: boolean; s3loc: string };
  };
  allComplete: boolean;
}

// User
export interface User {
  userid: string;
  userhandle: string;
  email: string;
  username: string;
}

// Import Types
export interface ImportUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface ImportProcessResponse {
  resumejson: ResumeJSON;
  warnings?: string[];
  source: 'yaml' | 'json' | 'pdf';
}

// Organization Types
export interface Organization {
  org_id: string;
  org_name: string;
  billing_contact?: string;
  billing_email?: string;
  renewal_date?: string;
  member_count?: number;
  status?: 'active' | 'inactive' | 'suspended';
  created_at?: string;
  updated_at?: string;
}

export interface JoinOrganizationResponse {
  message: string;
  organization: {
    org_id: string;
    org_name: string;
  };
}
